import { useSnackbar } from 'notistack'
import { useContext, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AddEditAdUnitContext } from 'features/inventory/ad-unit/contexts/AddEditAdUnitContext'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { mapGraphQLErrors } from 'providers/tanstack-query/utils'
import { floatPercentToDTO } from 'utils/number'
import { Schema } from '../schema'
import { UPDATE_AD_UNIT_VIEWABILITY_TOOLS } from './api/mutation'
import { Data, Variables } from './api/types'
import { UseUpdateAdUnitViewabilityTools } from './types'

export const useUpdateAdUnitViewabilityTools = (): UseUpdateAdUnitViewabilityTools => {
  const { t } = useTranslation('features/adUnit')
  const { enqueueSnackbar } = useSnackbar()
  const { adUnit, setLoading } = useContext(AddEditAdUnitContext)
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})

  if (!adUnit) {
    throw Error('Ad unit should be accessible')
  }

  const { mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_AD_UNIT_VIEWABILITY_TOOLS,
    onCompleted: async (data: Data) => {
      const {
        updateAdUnitViewabilityTool: { errors },
      } = data

      setLoading(false)

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.error.update'), { variant: 'error' })
      } else {
        setErrors({})
        enqueueSnackbar(t('form.success.update'), { variant: 'success' })
      }
    },
    queriesToInvalidate: ['adUnit'],
  })

  const toDTO = (schema: Schema): Variables => {
    const {
      adHoldingAutoEnabled,
      adHoldingEnabled,
      adUnitId,
      customizeLazyLoadingParametersEnabled,
      gamAdRequestMargin,
      kvTargetingAndPrebidAuction,
      lazyLoadingAutoEnabled,
      lazyLoadingEnabled,
      lazyLoadTargetingPrebidAuctionEnabled,
      mobileScaling,
      scrollSlowingAutoEnabled,
      scrollSlowingEnabled,
      viewabilityToolsEnabled,
    } = schema

    return {
      adHoldingAutoEnabled,
      adHoldingEnabled,
      adUnitId,
      customizeLazyLoadingParametersEnabled,
      gamAdRequestMargin: floatPercentToDTO(gamAdRequestMargin),
      kvTargetingAndPrebidAuction: floatPercentToDTO(kvTargetingAndPrebidAuction),
      lazyLoadingAutoEnabled,
      lazyLoadingEnabled,
      lazyLoadTargetingPrebidAuctionEnabled,
      mobileScaling,
      scrollSlowingAutoEnabled,
      scrollSlowingEnabled,
      viewabilityToolsEnabled,
    }
  }

  const update = (schema: Schema): void => {
    setLoading(true)

    mutateAsync({
      input: toDTO(schema),
    })
  }

  return { errors, update }
}
